import { produce } from "immer";
import { matchPath } from "react-router";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { getCurrentRouteWithoutBasename, pushRoute } from "@/app-utils/route.ts";
import { type BlastJob, type JobResult, JobStatusEnum, type PlaybookEvent } from "@/graphql.ts";
import { SubscriptionTypeEnum } from "@/modules/notifications/utils/types.ts";
import { BLAST_METHOD } from "@/settings/settings.ts";
import { webSocketClient } from "@/tools.js";
import type WebApiClient from "../../api/WebApiClient.js";
import {
  CLEAR_INITIAL_JOB,
  CLEAR_JOB,
  JOB_SUBSCRIPTION_FAILURE,
  JOB_SUBSCRIPTION_START,
  JOB_SUBSCRIPTION_STOP,
  LOAD_JOB_FAILURE,
  LOAD_JOB_REQUEST,
  LOAD_JOB_RESULT_FAILURE,
  LOAD_JOB_RESULT_REQUEST,
  LOAD_JOB_RESULT_SUCCESS,
  LOAD_JOB_SUCCESS,
  MARK_JOB_AS_READ,
  MODIFY_EVENT_SUCCESS,
  RERUN_JOB,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { type ReduxAction, createReducer } from "../redux-utils.js";
import { showNavbarElement } from "./navbar.js";
import { markAsRead } from "./notifications.js";

/**
 * State definition
 */
export interface IJobState {
  job: BlastJob;
  jobResults: IJobResultMap;
  isLoading: boolean;
  isResultLoading: boolean;
  jobSubscriptionId: string;
  initialJobForRerun: BlastJob;
}

const initialState: IJobState = {
  job: null,
  jobResults: {},
  isLoading: false,
  isResultLoading: false,
  jobSubscriptionId: null,
  initialJobForRerun: null,
};

// This map contains the full job result with ALL BLAST hits
export type IJobResultMap = Record<number, JobResult>; // map by queryId

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_JOB_REQUEST]: (state: IJobState, jobId: number): IJobState => {
    // if the jobId is not the same we wipe out the job data
    const job: BlastJob = state.job && state.job.id === jobId ? state.job : null;
    return {
      ...state,
      isLoading: true,
      job,
    };
  },

  [LOAD_JOB_SUCCESS]: (state: IJobState, job: BlastJob): IJobState => {
    return {
      ...state,
      isLoading: false,
      job,
    };
  },

  [LOAD_JOB_FAILURE]: (state: IJobState, _payload): IJobState => {
    return {
      ...state,
      isLoading: false,
      job: null,
    };
  },

  [LOAD_JOB_RESULT_REQUEST]: (state: IJobState, _jobId: number): IJobState => {
    return { ...state, isResultLoading: true };
  },

  [LOAD_JOB_RESULT_SUCCESS]: (state: IJobState, jobResult: JobResult): IJobState => {
    return produce(state, (draft) => {
      if (jobResult.id) {
        draft.jobResults[jobResult.id] = jobResult;
      }
      draft.isResultLoading = false;
    });
  },

  [LOAD_JOB_RESULT_FAILURE]: (state: IJobState, _payload): IJobState => {
    return { ...state, isResultLoading: false };
  },

  [JOB_SUBSCRIPTION_START]: (state: IJobState, payload: number) => {
    return { ...state, jobSubscriptionId: payload };
  },

  [JOB_SUBSCRIPTION_STOP]: (state: IJobState) => {
    return { ...state, jobSubscriptionId: null };
  },

  [CLEAR_JOB]: (_state: IJobState): IJobState => {
    return { ...initialState };
  },

  [RERUN_JOB]: (state: IJobState, job: BlastJob): IJobState => {
    return { ...state, initialJobForRerun: job };
  },

  [CLEAR_INITIAL_JOB]: (state: IJobState): IJobState => {
    return { ...state, initialJobForRerun: null };
  },

  [MARK_JOB_AS_READ]: (state: IJobState): IJobState => {
    return produce(state, (draft) => {
      draft.job.notification!.read = true;
    });
  },

  [MODIFY_EVENT_SUCCESS]: (state: IJobState, payload: PlaybookEvent) => {
    if (state.job?.id) {
      return produce(state, (draft) => {
        draft.job = { ...draft.job, eventName: payload.alias, eventDescription: payload.note, event: { ...payload } };
      });
    }
    return { ...state };
  },
});

/**
 * Action creators
 */
export function loadAndWatchJob(jobId: number) {
  return (dispatch, getState): Promise<BlastJob> => {
    const state: IAppState = getState();
    const jobState: IJobState = state.job;

    if (jobState.jobSubscriptionId && jobState.jobSubscriptionId !== jobId) {
      dispatch(unwatchJobStatus(jobState.jobSubscriptionId));
    }

    return dispatch(loadJob(jobId)).then((job: BlastJob) => {
      if (job.status !== JobStatusEnum.Completed && job.status !== JobStatusEnum.Error) {
        dispatch(watchJobStatus(jobId));
      }
      return job;
    });
  };
}

export function loadJob(jobId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<BlastJob> => {
    const state: IAppState = getState();
    const jobState: IJobState = state.job;

    if (!jobState.isLoading) {
      dispatch({ type: LOAD_JOB_REQUEST, payload: jobId });

      // Fetch job including all job results but only best BLAST hit
      return webApiClient
        .fetchJob(jobId, true)
        .then((response: BlastJob) => {
          dispatch({
            type: LOAD_JOB_SUCCESS,
            payload: response,
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_JOB_FAILURE,
            alertTitle: "Fetching of Job Failed",
          }),
        );
    } else {
      return Promise.resolve(state.job.job);
    }
  };
}

export function loadFullJobResult(jobId: number, queryId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<JobResult[]> => {
    const state: IAppState = getState();
    const jobState: IJobState = state.job;

    if (!getFullJobResult(jobState.jobResults, queryId)) {
      dispatch({ type: LOAD_JOB_RESULT_REQUEST, payload: jobId });

      // Fetch job result for this query including ALL hits
      return webApiClient
        .fetchJobResults(jobId, [queryId], false)
        .then((response: JobResult[]) => {
          dispatch({
            type: LOAD_JOB_RESULT_SUCCESS,
            payload: response[0],
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_JOB_RESULT_FAILURE,
            alertTitle: "Fetching of Job Results Failed",
          }),
        );
    }
  };
}

export function watchJobStatus(jobId: number): ReduxAction<void> {
  return (dispatch, getState) => {
    const subscriptionId = webSocketClient.subscribe({
      subscription: { eventType: SubscriptionTypeEnum.Job, variables: { jobId } },
      onMessage(message) {
        // Reload the job every time its status changes
        const jobPromise: Promise<BlastJob> = dispatch(loadJob(message.jobId));

        if (message.status === JobStatusEnum.Completed || message.status === JobStatusEnum.Error) {
          // if the job has terminated we push the user to the next job page (either summary or results)...
          jobPromise.then((job: BlastJob) => {
            // .. but only if he is not already browsing through the job !
            if (!matchPath({ path: `/job/${job.id.toString()}`, end: false }, getCurrentRouteWithoutBasename())) {
              pushRoute(getJobPath(job));
            }
          });
          // Stop job status subscription if the job has terminated
          if (getState().job.jobSubscriptionId) {
            dispatch(unwatchJobStatus(getState().job.jobSubscriptionId));
          }
        }
      },
      onError: createErrorHandler(dispatch, {
        actionType: JOB_SUBSCRIPTION_FAILURE,
        showAlert: false,
      }),
    });

    dispatch({
      type: JOB_SUBSCRIPTION_START,
      payload: subscriptionId,
    });
  };
}

export function clearAndUnwatchJob(): ReduxAction<void> {
  return (dispatch, getState) => {
    // Unsubscribe from job status updates
    const state: IJobState = getState().job;
    if (state.jobSubscriptionId) {
      dispatch(unwatchJobStatus(state.jobSubscriptionId));
    }

    // Clear the redux state
    dispatch({
      type: CLEAR_JOB,
    });
  };
}

export function unwatchJobStatus(subscriptionId: string): ReduxAction<void> {
  return (dispatch) => {
    webSocketClient.unsubscribe(subscriptionId);
    dispatch({
      type: JOB_SUBSCRIPTION_STOP,
    });
  };
}

export function markJobAsRead(jobId: number) {
  return (dispatch, getState) => {
    const job: BlastJob = getState().job.job;
    if (job?.notification && job.id === jobId && !job.notification.read) {
      // Mark notification as read and perform optimistic update of the job notification's 'read' attribute
      dispatch({ type: MARK_JOB_AS_READ });
      dispatch(markAsRead(job.notification.id));
    }
  };
}

export function clearInitialJob() {
  return {
    type: CLEAR_INITIAL_JOB,
  };
}

export function rerunJob(jobId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient) => {
    return webApiClient
      .fetchJobForRerun(jobId)
      .then((job: BlastJob) => {
        dispatch({
          type: RERUN_JOB,
          payload: job,
        });
        dispatch(showNavbarElement({ showBlastPlaybookWizard: true }));
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_JOB_RESULT_FAILURE,
          alertTitle: `Fetching of Job ${jobId} Failed`,
        }),
      );
  };
}

/**
 * Helpers
 */
export function isPending(job: BlastJob): boolean {
  return job.status !== JobStatusEnum.Completed && job.status !== JobStatusEnum.Error;
}

export function isError(job: BlastJob): boolean {
  return job.status === JobStatusEnum.Error;
}

export function getJobPath(job: BlastJob): string {
  if (job.status === JobStatusEnum.Completed) {
    return job.resultCount > 1 ? `/job/${job.id}/results` : `/job/${job.id}/results/${job.firstResultId}`;
  }
  return `/job/${job.id}/summary`;
}

export function isBlastJob(job: BlastJob): string {
  return Object.values(BLAST_METHOD).find((m: string) => m.toUpperCase() === job.tool.toUpperCase());
}

/**
 * Search and return the full result for resultId from a blast job.
 *   Returns IJobResult if resultId is found.
 *   Otherwise returns undefined.
 * @param jobResultMap map with full job results
 * @param resultId     number, the id of the result in the job
 * @returns {JobResult}, has a value if the result exist
 */
export function getFullJobResult(jobResultMap: IJobResultMap, resultId: number): JobResult {
  return jobResultMap[resultId];
}
